import type { FunctionComponent } from 'preact';
import { useCallback, useRef, useState } from 'preact/hooks';
import { useDraggable } from '../../hooks/useDraggable.js';
import styles from './StatusBadge.module.css';
import cx from 'classnames';
import { Button } from '../Button/Button.js';
import { useTaskStats } from '../CloudpackProvider/useTaskStats.js';
import { elementIds } from '../../constants.js';
import type { PageLoadTimeReport } from '../../hooks/usePageLoadTimeReporter.js';
import type { TaskStats } from '@ms-cloudpack/api-server/browser';

export interface StatusBadgeProps {
  /** Callback when the dialog is opened */
  onOpenDialog: () => void;
  pageLoadTimeReport: PageLoadTimeReport;
}

const badgeStatusKey = 'cloudpack.badgeStatus';
const buttonHint = '(see details)';
const buttonHelp = 'Show bundle result details';

export const StatusBadge: FunctionComponent<StatusBadgeProps> = ({ onOpenDialog, pageLoadTimeReport }) => {
  const badgeRootRef = useRef<HTMLDivElement>(null);
  const gripRef = useRef<HTMLDivElement>(null);
  const stats = useTaskStats();
  const [isBadgeCollapsed, setIsBadgeCollapsed] = useState<boolean>(!!localStorage.getItem(badgeStatusKey));

  const shouldRender = stats.status !== 'unknown';

  const toggleBadgeCollapsed = useCallback(() => {
    setIsBadgeCollapsed((prev) => {
      const newCollapsedState = !prev;
      localStorage.setItem(badgeStatusKey, newCollapsedState ? 'collapsed' : '');
      return newCollapsedState;
    });
  }, []);

  useDraggable({
    // If we're not yet able to render, don't enable dragging yet.
    // Otherwise the event handlers won't be attached when the component renders.
    enabled: shouldRender,
    containerElementRef: badgeRootRef,
    dragElementRef: gripRef,
  });

  if (!shouldRender) {
    return null;
  }

  const { buttonStatusText, statusClass, statsText } = processStats(stats, pageLoadTimeReport);
  // WARNING: If you change this text, also update the `name` used to find the button in
  // apps/test-start-e2e/src/utilities/verifyOverlay.ts
  const buttonText = 'Cloudpack: ' + buttonStatusText;
  const buttonAriaLabel = `${buttonText.replace(buttonHint, '')}. ${buttonHelp}`;
  const collapseButtonText = isBadgeCollapsed ? 'Expand Cloudpack badge' : 'Collapse';

  const badge = (
    <div id={elementIds.statusBadgeRoot} ref={badgeRootRef} class={cx(styles.badge, statusClass)}>
      {!isBadgeCollapsed && (
        <>
          <div ref={gripRef} class={styles.gripArea} aria-hidden>
            <div class={styles.grip} />
            <div class={styles.grip} />
            <div class={styles.grip} />
          </div>
          <Button
            variant="custom"
            class={styles.button}
            onClick={onOpenDialog}
            title={buttonHelp}
            aria-label={buttonAriaLabel}
          >
            <div class={styles.buttonText}>{buttonText}</div>
          </Button>
          <div>{statsText}</div>
        </>
      )}
      <Button
        variant="iconOnly"
        class={cx(styles.chevron, isBadgeCollapsed && styles.collapsed)}
        onClick={toggleBadgeCollapsed}
        title={collapseButtonText}
        aria-label={collapseButtonText}
      >
        »
      </Button>
    </div>
  );

  return (
    <div class={styles.fixedOverlay}>
      <div class={styles.contentSurface}>{badge}</div>
    </div>
  );
};

/**
 * Based on the status, get the badge color, button text, and stats text.
 */
function processStats(
  stats: TaskStats,
  pltReport: PageLoadTimeReport,
): {
  /** class (color) for the badge root */
  statusClass: string | undefined;
  /** short status text for the button to open the overlay */
  buttonStatusText: string;
  /** stats to display after the button, like "50% completed, 2 errors, 3 warnings in 500ms" */
  statsText: string;
} {
  const { status, totalTasks, remainingTasks, totalErrors, totalWarnings } = stats;

  let statusClass: string | undefined;
  let buttonStatusText: string;
  let statsText = '';

  if (status === 'idle' && totalTasks) {
    [statusClass, buttonStatusText] = totalErrors
      ? [styles.error, `errors ${buttonHint}`]
      : totalWarnings
        ? [styles.warning, `warnings ${buttonHint}`]
        : [styles.success, 'success'];
  } else {
    // no styling for this case
    buttonStatusText = status === 'pending' ? 'bundling...' : 'idle';
  }

  if (totalTasks) {
    // "Number of tasks" is likely not a meaningful metric for users (and doesn't strictly correspond to
    // number of packages), so just show either estimated percentage or "completed" plus any issue counts.
    const taskStats = [
      remainingTasks === 0
        ? 'completed'
        : `${(((totalTasks - remainingTasks) / totalTasks) * 100).toFixed(1)}% completed`,
      totalErrors && `${totalErrors} error${totalErrors > 1 ? 's' : ''}`,
      totalWarnings && `${totalWarnings} warning${totalWarnings > 1 ? 's' : ''}`,
    ]
      .filter(Boolean)
      .join(', ');

    const pltStat = pltReport.status === 'success' ? ` in ${Math.round(pltReport.pageLoadTime) ?? '??'}ms` : '';

    statsText = taskStats + pltStat;
  }

  return { statusClass, buttonStatusText, statsText };
}
